import type { AppStoreDto } from '@/interfaces/service/appstore/appstore.interface';
import type { CreateAppStoreResult } from '@/api/appstore.api';
import { INVALID_REQUEST_CODE, STORE_ALREADY_REGISTERED_CODE } from '@/constants/error/api.error.constants';

/**
 * 병원 선택 → 조회 → (없으면) 등록 → 매장 전환 → 이동 까지의 흐름.
 *
 * 화면에서 떼어낸 이유는 하나다 — 이 흐름의 결함이 전부 **비동기 이탈 경로**에 있었기 때문이다.
 * 전체 화면을 덮는 인디케이터를 켜 놓고 실패하면 내리는 코드가 없었고, 응답을 기다리는 사이
 * 모달이 닫히면 주인 없는 인디케이터가 남았다. 이런 건 렌더 결과가 아니라 **호출 순서** 로만
 * 검증되므로, DOM 없이 돌릴 수 있게 의존성을 주입받는 형태로 분리한다.
 */
export interface StoreRegistrationDeps {
  findStore: (query: { storeName: string; storeDetail: string }) => Promise<AppStoreDto | undefined>;
  createStore: (query: { storeName: string; storeDetail: string }) => Promise<CreateAppStoreResult>;
  /** 실패하면 거절해야 한다. TanStack 의 mutate 가 아니라 mutateAsync 를 넘겨야 한다는 뜻이다. */
  switchStore: (params: { storeId: number; type: 'CHANGE' | 'REGISTRATION' }) => Promise<unknown>;
  setIndicator: (show: boolean) => void;
  notify: (text: string) => void;
  /** 조회·등록으로 확보한 매장을 앱 상태에 반영한다. isNew 면 크롤링 대기 목록에도 넣는다. */
  applyStore: (store: AppStoreDto, isNew: boolean) => void;
  /** isNew 면 방금 등록한 병원이다 — 이동 대상이 다르다. */
  navigate: (store: AppStoreDto, isNew: boolean) => void;
  /** 인증 토큰 보유 여부. 없으면 매장 전환을 건너뛴다. */
  hasToken: () => boolean;
  /**
   * 이 흐름을 계속 진행해도 되는지. await 재개마다 확인한다.
   * 언마운트뿐 아니라 **모달이 닫힌 경우**도 false 여야 한다 — 호출부에 따라 모달은
   * 상시 마운트된 채 표시 여부만 바뀌므로, 마운트 여부만 보면 취소를 놓친다.
   */
  isActive: () => boolean;
}

/**
 * 실행 식별자 + 전역 인디케이터 소유권.
 *
 * 전역 인디케이터는 boolean 하나뿐이라 "누가 켰는지" 를 스스로 기억하지 못한다. 그리고 이 모달은
 * 닫았다 다시 열 수 있어서, 취소된 요청이 나중에 응답을 받고 되살아나 새 요청의 상태를 덮을 수 있다.
 * 두 문제 모두 "이 응답이 어느 실행의 것인가" 를 구분하면 사라진다.
 *
 * - begin()   새 실행을 시작한다. 이전 실행은 그 즉시 무효가 되고 인디케이터 소유권도 회수한다
 *              (회수했으면 hadOwner=true — 호출부가 전역 인디케이터를 내려야 한다는 뜻이다).
 * - cancel()  진행 중이던 실행을 무효로 만든다(닫힘·언마운트).
 * - isCurrent(id) 그 실행이 아직 유효한가.
 * - claim(id) / release(id) 인디케이터 소유권. 소유자만 내릴 수 있다.
 */
export const createRunGate = () => {
  let latest = 0;
  let owner = 0;

  return {
    begin: () => {
      latest += 1;

      // 이전 실행이 켜 둔 인디케이터의 소유권을 회수한다. 그 실행은 방금 무효가 됐으므로
      // 다시 돌아와 스스로 내리지 못한다 — 여기서 회수하지 않으면 백드롭이 갇힌다.
      const hadOwner = owner !== 0;

      owner = 0;

      return { id: latest, hadOwner };
    },
    cancel: () => {
      latest += 1;
    },
    isCurrent: (id: number) => id === latest,
    claim: (id: number) => {
      owner = id;
    },
    /** 소유자였으면 true 를 주고 소유권을 놓는다. 아니면 아무것도 하지 않는다. */
    release: (id: number) => {
      if (owner !== id) {
        return false;
      }

      owner = 0;

      return true;
    },
    /** 지금 누군가 인디케이터를 켜 둔 상태인가. 닫힘·언마운트 정리에 쓴다. */
    releaseAny: () => {
      if (owner === 0) {
        return false;
      }

      owner = 0;

      return true;
    }
  };
};

/** 등록 직후에는 크롤링 전이라도 대시보드로 보낸다. 기존 병원은 크롤링이 끝난 것만 대시보드다. */
export const shouldOpenDashboard = (store: { status?: string }, isNew: boolean) => isNew || store.status === 'STABLE';

const GENERIC_FAILURE = '병원 등록에 실패했습니다. 잠시 후 다시 시도해주세요.';

/** 등록 실패 안내. 원인별로 사용자가 다음에 할 일이 달라 분기한다. */
export const getRegistrationFailureMessage = (result: { code?: string }) => {
  switch (result.code) {
    case STORE_ALREADY_REGISTERED_CODE:
      // 시스템 오류가 아니라 "이미 있다" 는 사실 통보다.
      return '이미 등록된 병원입니다.';
    case INVALID_REQUEST_CODE:
      return '병원 정보가 올바르지 않습니다. 다시 검색해 선택해주세요.';
    default:
      return GENERIC_FAILURE;
  }
};

/** 선택 값에서 조회·등록에 쓸 이름과 주소를 뽑는다. 못 쓰는 값이면 사유를 돌려준다. */
export const resolveSelectedStore = (selectedStore: { name?: string; address?: string } | undefined | null) => {
  // 버튼 활성 조건은 채널이 아무거나 선택되면 참이 되는데 이 흐름이 쓰는 값은 NAVER 하나뿐이다.
  // 다른 채널만 고르면 여기서 끝나므로, 버튼이 죽은 것처럼 보이지 않게 사유를 남긴다.
  if (!selectedStore) {
    return { ok: false as const, message: '네이버 검색 결과에서 병원을 선택해주세요.' };
  }

  // addressConverter 는 이름을 못 찾으면 빈 문자열을 넣는다(`?? ''`).
  // 그대로 보내면 서버가 필수값 누락으로 거절하는데 사용자에게는 원인이 안 보인다.
  if (!selectedStore.name?.trim()) {
    return { ok: false as const, message: '병원 이름을 확인할 수 없습니다. 다시 검색해 선택해주세요.' };
  }

  return { ok: true as const, storeName: selectedStore.name, storeDetail: selectedStore.address ?? '' };
};

/**
 * 인디케이터 소유권. 전역 스토어는 boolean 하나뿐이라 "내가 켠 것" 을 스스로 기억해야 한다.
 * 켠 적 없는데 끄면 다른 화면이 켜 둔 인디케이터를 대신 꺼버린다.
 */
const makeIndicator = (deps: StoreRegistrationDeps) => {
  let owned = false;

  return {
    show: () => {
      owned = true;
      deps.setIndicator(true);
    },
    release: () => {
      if (!owned) {
        return;
      }

      owned = false;
      deps.setIndicator(false);
    }
  };
};

/** 안내는 부수효과다. 여기서 던지는 것이 흐름 전체를 거절시키면 안 된다. */
const safeNotify = (deps: StoreRegistrationDeps, message: string) => {
  try {
    deps.notify(message);
  } catch (error) {
    console.error('store registration notify failed:', error);
  }
};

/** 인디케이터가 화면을 덮고 있으므로 먼저 내린 뒤 알려야 안내가 가려지지 않는다. */
const fail = (indicator: { release: () => void }, deps: StoreRegistrationDeps, message: string) => {
  indicator.release();
  safeNotify(deps, message);
};

/** 전환 성공 여부. 실패는 여기서 안내까지 마치고 false 를 돌려준다. */
const switchStore = async (store: AppStoreDto, type: 'CHANGE' | 'REGISTRATION', deps: StoreRegistrationDeps, indicator: { release: () => void }, failureMessage: string): Promise<boolean> => {
  try {
    await deps.switchStore({ storeId: store.storeId, type });

    return true;
  } catch (error) {
    // mutate 가 아니라 mutateAsync 를 받으므로 실제 거절이 여기로 온다.
    // 잡지 않으면 클릭 핸들러에서 소비되지 않은 rejection 이 된다.
    console.error('store switch failed:', error);

    // 이탈했더라도 우리가 켠 인디케이터는 놓아야 한다. 소유자가 아니면 release 가 알아서 무시한다.
    indicator.release();

    if (deps.isActive()) {
      safeNotify(deps, failureMessage);
    }

    return false;
  }
};

export const runStoreRegistration = async (selectedStore: { name?: string; address?: string } | undefined | null, deps: StoreRegistrationDeps): Promise<void> => {
  const resolved = resolveSelectedStore(selectedStore);

  if (!resolved.ok) {
    safeNotify(deps, resolved.message);

    return;
  }

  const indicator = makeIndicator(deps);
  const query = { storeName: resolved.storeName, storeDetail: resolved.storeDetail };

  try {
    const existing = await deps.findStore(query);

    // 조회를 기다리는 사이 사용자가 닫았다면 여기서 끝낸다. 인디케이터는 아직 켜지 않았으므로
    // 정리할 것도 없고, 취소한 등록을 대신 진행해서도 안 된다.
    if (!deps.isActive()) {
      return;
    }

    if (existing && existing.storeId > -1) {
      deps.applyStore(existing, false);

      if (deps.hasToken() && !(await switchStore(existing, 'CHANGE', deps, indicator, '병원 전환에 실패했습니다. 잠시 후 다시 시도해주세요.'))) {
        return;
      }

      if (!deps.isActive()) {
        return;
      }

      deps.navigate(existing, false);

      return;
    }

    indicator.show();

    const created = await deps.createStore(query);

    // 인디케이터를 켠 뒤의 이탈이다. 여기서는 우리가 켠 것이 확실하므로 직접 내린다.
    if (!deps.isActive()) {
      indicator.release();

      return;
    }

    if (!created.isSuccess) {
      fail(indicator, deps, getRegistrationFailureMessage(created));

      return;
    }

    const newStore = created.appStore;

    if (!(newStore?.storeId > -1)) {
      // 등록됐다는데 식별자가 없으면 이동할 곳이 없다. 조용히 멈추지 않는다.
      fail(indicator, deps, GENERIC_FAILURE);

      return;
    }

    deps.applyStore(newStore, true);

    // 여기서 실패해도 **등록 자체는 이미 성공했다.** "등록에 실패했다" 고 말하면 거짓이다.
    if (deps.hasToken() && !(await switchStore(newStore, 'REGISTRATION', deps, indicator, '병원은 등록됐지만 전환에 실패했습니다. 잠시 후 다시 시도해주세요.'))) {
      return;
    }

    if (!deps.isActive()) {
      indicator.release();

      return;
    }

    // 이동하는 동안에는 인디케이터를 유지한다. 정리는 호출부가 맡는다 —
    // 경로가 바뀌면 전역 Indicator 가, 화면이 사라지거나 닫히면 cleanup 이 내린다.
    deps.navigate(newStore, true);
  } catch (error) {
    // findStore 가 거절하는 경우까지 여기서 받는다 — 이 함수는 절대 거절하지 않는다.
    console.error('store registration failed:', error);

    if (deps.isActive()) {
      fail(indicator, deps, GENERIC_FAILURE);
    } else {
      indicator.release();
    }
  }
};
